/**
 * What a scan row offers, wherever it is asked.
 *
 * Split out of `ScanRowMenu` so the three-dot button at the end of the row and
 * the right-click on the row itself cannot drift into being two different menus.
 *
 * "Scan again" is the one entry that is not in the three-dot menu's history:
 * opening a stored scan and refetching the site are two different intentions,
 * and the row's own button only ever meant the first. A week-old row is exactly
 * where the second question gets asked.
 */
import { Building2, Copy, ExternalLink, Mail, Phone, PhoneOutgoing, RefreshCw, ScanLine, Trash2 } from "lucide-react";
import type { ScanListItem } from "../../../../lib/siteScan";
import { useCallContext } from "../../../telephony/CallProvider";
import { useAiTestCall } from "../../../telephony/aiTestCall";
import { useCopyToast } from "../../../ui";
import type { T } from "../types";

export function ScanRowItems({
  row,
  canManage,
  t,
  close,
  head,
  onOpen,
  onRescan,
  onDelete,
}: {
  row: ScanListItem;
  canManage: boolean;
  t: T;
  close: () => void;
  /** The right-click menu says which row it opened from; the row's own button
   *  sits ON that row and does not have to. */
  head?: boolean;
  onOpen: () => void;
  onRescan: () => void;
  onDelete: () => void;
}) {
  const copy = useCopyToast();
  const call = useCallContext();
  const aiCall = useAiTestCall();
  const canCall = Boolean(call?.enabled) && Boolean(row.phone);

  const act = (fn: () => void) => {
    close();
    fn();
  };

  return (
    <>
      {head ? (
        <div className="crm-menu-head">
          <div className="crm-menu-t">{row.companyName ?? row.domain}</div>
          {/* Only when it adds something. Half these rows have no matched
              company, and a heading that says the domain twice reads as a
              rendering fault. */}
          {row.companyName ? <div className="crm-menu-s">{row.domain}</div> : null}
        </div>
      ) : null}

      <button type="button" className="crm-mi" onClick={() => act(onOpen)}>
        <ScanLine className="size-4" />
        {t("scanner.table.openScan")}
      </button>

      <button type="button" className="crm-mi" onClick={() => act(onRescan)}>
        <RefreshCw className="size-4" />
        {t("scanner.action.rescan")}
      </button>

      {/* The dialog shows what the AI would say about this site before a test
          number rings; the brain's arguments come in when a run exists. */}
      {aiCall.canCall ? (
        <button
          type="button"
          className="crm-mi"
          onClick={() => act(() => aiCall.open({ domain: row.domain, regCode: row.regCode, name: row.companyName }))}
        >
          <PhoneOutgoing className="size-4" />
          {t("aiOutbound.testCall.menu")}
        </button>
      ) : null}

      <a className="crm-mi" href={row.url} target="_blank" rel="noreferrer noopener" onClick={close}>
        <ExternalLink className="size-4" />
        {t("scanner.table.openSite")}
      </a>

      <button type="button" className="crm-mi" onClick={() => act(() => copy(row.domain))}>
        <Copy className="size-4" />
        {t("leads.copyDomain")}
      </button>

      {row.email ? (
        <button type="button" className="crm-mi" onClick={() => act(() => copy(row.email!))}>
          <Mail className="size-4" />
          {t("common.copyEmail")}
        </button>
      ) : null}

      {canCall ? (
        <button
          type="button"
          className="crm-mi"
          onClick={() =>
            act(() =>
              call?.startCall({
                phone: row.phone!,
                companyName: row.companyName ?? undefined,
                regCode: row.regCode ?? undefined,
                leadDomain: row.domain,
                source: "scanner",
              }),
            )
          }
        >
          <Phone className="size-4" />
          {t("common.call")}
        </button>
      ) : null}

      {row.regCode ? (
        <button type="button" className="crm-mi" onClick={() => act(() => copy(row.regCode!))}>
          <Building2 className="size-4" />
          {t("common.copyRegCode")}
        </button>
      ) : null}

      {canManage ? (
        <>
          <div className="crm-menu-sep" />
          <button type="button" className="crm-mi crm-mi-danger" onClick={() => act(onDelete)}>
            <Trash2 className="size-4" />
            {t("scanner.table.delete")}
          </button>
        </>
      ) : null}
    </>
  );
}
